import { useMutation } from "@apollo/client/react";
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import type { StoreApi, UseBoundStore } from "zustand";

import { Mutations } from "@/constants";
import type { NodeStore } from "@/components/@types";
import type { RuleFragment } from "@/__generated__/graphql";
import { Skeleton } from "@/components/ui/skeleton";

import { RuleConditionsActionsView } from "./RuleEditor";
import { serializeFilterGroup } from "./utils/filterGroup";
import type { FilterGroup } from "./types/rule";

function isHydratedRule(node: unknown): node is RuleFragment {
  if (node == null || typeof node !== "object") {
    return false;
  }
  const candidate = node as RuleFragment;
  return (
    candidate.__typename === "RuleDefinitionObject" &&
    typeof candidate.id === "string" &&
    typeof candidate.identifier === "string"
  );
}

function normalizeRule(rule: RuleFragment): RuleFragment {
  return {
    ...rule,
    suggestions: rule.suggestions ?? [],
    conditions: rule.conditions ?? [],
  };
}

function RuleObjectPanelSkeleton() {
  return (
    <div className="p-5 flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Skeleton variant="text" level="title-lg" className="w-25" />
        <Skeleton variant="rectangular" className="rounded-md h-9 w-full" />
        <Skeleton variant="rectangular" className="rounded-md h-9 w-full" />
      </div>
      <div className="flex flex-col gap-2">
        <Skeleton variant="text" level="title-lg" className="w-20" />
        <Skeleton variant="rectangular" className="rounded-md h-9 w-full" />
      </div>
    </div>
  );
}

type RuleObjectPanelProps = {
  store: UseBoundStore<StoreApi<NodeStore>>;
  isLoading?: boolean;
};

/**
 * Custom rule editor for the node panel. Reads the hydrated rule from the
 * shared ActivePanelNode store (NODE_QUERY_WITH_PENDING_CHANGE) — same path as
 * every other object panel — instead of issuing a separate query.
 */
export function RuleObjectPanel({ store, isLoading }: RuleObjectPanelProps) {
  const panelNode = useSyncExternalStore(
    (onStoreChange) => store.subscribe(onStoreChange),
    () => store.getState().fieldValues ?? null,
    () => null,
  );
  const serverRule = isHydratedRule(panelNode) ? panelNode : null;
  const [draft, setDraft] = useState<RuleFragment | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setDraft(null);
  }, [serverRule?.id]);

  const baseRule =
    draft && serverRule && draft.id === serverRule.id ? draft : serverRule;
  const rule = baseRule ? normalizeRule(baseRule) : null;

  const ruleRef = useRef(rule);
  ruleRef.current = rule;

  useEffect(() => {
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, []);

  const [updateNodeMutate] = useMutation(Mutations.UPDATE_NODE);

  const persistFilters = useCallback(
    async (nextGroup: FilterGroup) => {
      const current = ruleRef.current;
      if (!current) return;
      try {
        const result: any = await updateNodeMutate({
          variables: {
            input: {
              id: current.id,
              node: {
                rule: {
                  conditions: serializeFilterGroup(nextGroup),
                },
              },
            },
          },
        });
        const updated = result?.data?.updateNode?.data?.[0];
        if (
          updated?.__typename === "RuleDefinitionObject" &&
          updated.conditions
        ) {
          setDraft((prev) =>
            prev && prev.id === current.id
              ? { ...prev, conditions: updated.conditions }
              : prev,
          );
        }
      } catch (err) {
        console.error("Failed to persist rule conditions:", err);
      }
    },
    [updateNodeMutate],
  );

  if (isLoading || !serverRule || !rule) {
    return <RuleObjectPanelSkeleton />;
  }

  return (
    <div
      className="flex-1 min-h-0 overflow-auto p-5"
      data-testid="rule-object-panel"
    >
      <RuleConditionsActionsView
        key={rule.id}
        rule={rule}
        onPersistFilters={persistFilters}
        onRuleChange={(updater) => {
          const current = ruleRef.current;
          if (!current) return;
          setDraft(updater(current));
        }}
      />
    </div>
  );
}
